/**
 * `useClickOutside` hook for capturing clicking outside of an element
 */
import { RefObject } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { coerceArray } from '../../utils/UNSAFE_arrayUtils';

type Props = {
  /**
   * If true outside click is disabled
   */
  isDisabled?: boolean;
  /**
   * Reference to element or array of elements relative to which outside click is performed
   */
  ref: RefObject<HTMLElement> | RefObject<HTMLElement>[];
  /**
   * Outside click callback
   */
  handler?: (event: MouseEvent) => void;
};

/**
 * Checks if the mouse event target is inside of any of the specified containers, or if it
 * occurs in a layer whose 'logicalParent' belongs to one of the containers.
 *
 * @param event a mouse event associated with the potential "outside click" action
 * @param ref an array of references to elements that are considered "inside"
 * @returns 'true' if the event occurs "outside" of the specified list of containers
 */
const isPointOutside = (event: Event, ref: RefObject<HTMLElement>[]) => {
  const target = event.target as HTMLElement;

  // if the event has no target, consider it "inside"
  if (!target) {
    return false;
  }

  const doc = getDocument(target);
  if (!doc.contains(target)) {
    return false;
  }

  // if the target is contained in any of the target containers, it is not "outside"
  if (ref.some((elRef) => elRef.current?.contains(target))) {
    return false;
  }

  // the event target can aslo occur in a popup layer that belongs to an "inside" container

  // get open layers under the layer host
  const layerHostElem = doc.querySelector('#__root_layer_host');
  let logicalParent: Element | null = null;

  if (layerHostElem) {
    const isTargetInLayer = layerHostElem.contains(target);

    if (isTargetInLayer) {
      for (let i = 0; i < layerHostElem.children.length; i++) {
        const layerNode = layerHostElem.children[i] as any;
        if (layerNode.contains(target)) {
          logicalParent = layerNode['__oj_logical_parent'];
          break;
        }
      }
    }
  }
  // check if layer's 'logicalParent' is inside of any of the "inside" containers
  return ref.every((elRef) => !elRef.current?.contains(logicalParent));
};

const getDocument = (node?: Node): Document => node?.ownerDocument ?? document;

export const useOutsideClick = ({ isDisabled: disabled = false, ref, handler }: Props) => {
  // use ref to store mutable data and not cause a re-render
  const stateRef = useRef({
    isMouseDown: false
  });

  const state = stateRef.current;

  useEffect(() => {
    const refs = coerceArray(ref);
    if (
      !handler ||
      disabled ||
      (refs.length > 0 && refs.findIndex((elRef) => elRef.current === null) > -1)
    )
      return;
    const onMouseDown = () => {
      state.isMouseDown = true;
    };

    const onMouseUp = (event: MouseEvent) => {
      if (state.isMouseDown && isPointOutside(event, refs)) {
        state.isMouseDown = false;
        handler?.(event);
      }
    };
    const firstElRef = refs[0];
    const doc = getDocument(firstElRef.current!);
    if (doc) {
      doc.addEventListener('mousedown', onMouseDown, true);
      doc.addEventListener('mouseup', onMouseUp, true);

      return () => {
        doc.removeEventListener('mousedown', onMouseDown, true);
        doc.removeEventListener('mouseup', onMouseUp, true);
      };
    }
    return undefined;
  }, [disabled, handler, ref, state]);
};
